<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Balanzas</h2>
        <p class="text-muted mb-0">
          Configura formatos de lectura para tickets pesables y códigos generados por balanza.
        </p>
      </div>
      <div class="d-flex gap-2">
        <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'MaintenanceOperational' }">
          Volver a Operativo
        </router-link>
        <button class="btn btn-dark btn-sm" @click="resetForm">
          Nuevo formato
        </button>
      </div>
    </header>

    <div v-if="!canManageScaleFormats" class="alert alert-warning mb-0">
      Esta subsección está disponible sólo para `admin`, `administrador` y `gerente`.
    </div>

    <template v-else>
      <div class="card shadow-sm">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Código</label>
              <input v-model="form.codigo" class="form-control" type="text" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Nombre</label>
              <input v-model="form.nombre" class="form-control" type="text" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Prefijo</label>
              <input v-model="form.prefijo" class="form-control" type="text" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Longitud total</label>
              <input v-model.number="form.longitud_total" class="form-control" type="number" min="1" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Activo</label>
              <select v-model="form.activo" class="form-select">
                <option :value="true">Sí</option>
                <option :value="false">No</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Inicio PLU</label>
              <input v-model.number="form.pos_codigo_inicio" class="form-control" type="number" min="0" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Largo PLU</label>
              <input v-model.number="form.pos_codigo_longitud" class="form-control" type="number" min="1" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Inicio peso</label>
              <input v-model.number="form.pos_peso_inicio" class="form-control" type="number" min="0" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Largo peso</label>
              <input v-model.number="form.pos_peso_longitud" class="form-control" type="number" min="1" />
            </div>
          </div>

          <div class="d-flex gap-2 mt-4">
            <button class="btn btn-dark" :disabled="saving" @click="saveFormat">
              {{ saving ? 'Guardando...' : (form.formato_id ? 'Actualizar formato' : 'Guardar formato') }}
            </button>
            <button class="btn btn-outline-secondary" :disabled="saving" @click="resetForm">
              Cancelar
            </button>
          </div>
        </div>
      </div>

      <div class="card shadow-sm">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
            <div>
              <h3 class="h6 mb-1">Formatos registrados</h3>
              <p class="text-muted small mb-0">Configuración global usada por ventas para resolver lecturas de balanza.</p>
            </div>
            <button class="btn btn-outline-dark btn-sm" :disabled="loading" @click="loadRows">
              Recargar
            </button>
          </div>

          <div v-if="loading" class="text-muted">Cargando formatos...</div>
          <div v-else-if="!rows.length" class="text-muted">No hay formatos configurados.</div>
          <div v-else class="list-group list-group-flush">
            <div v-for="row in rows" :key="row.formato_id" class="list-group-item px-0">
              <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <div class="fw-semibold">{{ row.nombre }}</div>
                  <small class="text-muted">
                    {{ row.codigo }} · prefijo {{ row.prefijo }} · largo {{ row.longitud_total }}
                  </small>
                  <div class="small text-muted">
                    PLU {{ row.pos_codigo_inicio }}/{{ row.pos_codigo_longitud }} · Peso {{ row.pos_peso_inicio }}/{{ row.pos_peso_longitud }}
                  </div>
                </div>
                <div class="d-flex gap-2 align-items-center flex-wrap justify-content-end">
                  <span :class="['badge', row.activo ? 'bg-success' : 'bg-secondary']">
                    {{ row.activo ? 'Activo' : 'Inactivo' }}
                  </span>
                  <button class="btn btn-outline-primary btn-sm" @click="editRow(row)">Editar</button>
                  <button class="btn btn-outline-danger btn-sm" @click="deleteRow(row)">Eliminar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import scaleFormatsService from '@/services/scaleFormatsService'

const loading = ref(false)
const saving = ref(false)
const rows = ref([])
const currentRoleName = computed(() => String(localStorage.getItem('rol_name') || '').trim().toLowerCase())
const canManageScaleFormats = computed(() => ['administrador', 'admin', 'gerente'].includes(currentRoleName.value))

const form = reactive({
  formato_id: null,
  codigo: '',
  nombre: '',
  prefijo: '',
  longitud_total: 13,
  pos_codigo_inicio: 2,
  pos_codigo_longitud: 5,
  pos_peso_inicio: 7,
  pos_peso_longitud: 5,
  unidad_peso: 'GRAMOS',
  activo: true
})

function resetForm() {
  Object.assign(form, {
    formato_id: null,
    codigo: '',
    nombre: '',
    prefijo: '',
    longitud_total: 13,
    pos_codigo_inicio: 2,
    pos_codigo_longitud: 5,
    pos_peso_inicio: 7,
    pos_peso_longitud: 5,
    unidad_peso: 'GRAMOS',
    activo: true
  })
}

function editRow(row) {
  Object.assign(form, { ...row })
}

async function loadRows() {
  if (!canManageScaleFormats.value) {
    rows.value = []
    return
  }
  loading.value = true
  try {
    rows.value = await scaleFormatsService.list()
  } catch (error) {
    console.error('Error cargando formatos de balanza', error)
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function saveFormat() {
  saving.value = true
  try {
    if (form.formato_id) {
      await scaleFormatsService.update(form.formato_id, form)
    } else {
      await scaleFormatsService.create(form)
    }
    resetForm()
    await loadRows()
    window.alert('Formato de balanza guardado')
  } catch (error) {
    console.error('Error guardando formato de balanza', error)
    window.alert('No se pudo guardar el formato de balanza')
  } finally {
    saving.value = false
  }
}

async function deleteRow(row) {
  try {
    await scaleFormatsService.remove(row.formato_id)
    if (form.formato_id === row.formato_id) {
      resetForm()
    }
    await loadRows()
    window.alert('Formato de balanza eliminado')
  } catch (error) {
    console.error('Error eliminando formato de balanza', error)
    window.alert('No se pudo eliminar el formato de balanza')
  }
}

onMounted(async () => {
  await loadRows()
})
</script>
